import React, { useState, useMemo } from "react";
import {
  ToolShell, Field, Select, fmt, UNITS,
  toBase, fromBase, altsFor,
  hazenWilliamsHeadLoss, pipeVelocity, npshAvailable,
  UnitNumber, FittingsPicker, fittingsEquivalentLength, fittingsSummary,
} from "./_shared";
import {
  PV_REGIONS, PUMP_SIZES_KW, PV_PANEL_WATTAGES,
  DAYLIGHT_HOURS, simulateSolarPumpDay,
} from "../../lib/content/solar";

/**
 *  PUMP SIZING WORKFLOW
 *  --------------------------------------------------------------
 *  One workspace, sub-calculators stacked. No re-typing across
 *  tools, no chasing values from one page to another.
 *
 *  Sections (top → bottom):
 *    0. Mode toggle — "Design new system" vs "Specify pump for
 *       existing system" (reverse-engineer the system from a
 *       measured operating point, then re-spec for a new flow).
 *    1. System hydraulics — single pipe by default, sub-main and
 *       lateral are collapsed accordions the user only opens if
 *       their network has those tiers. Each segment mirrors the
 *       standalone Hazen-Williams advanced tool 1:1 — same
 *       material list, unit selectors, fittings picker and
 *       velocity/head/bar outputs — so muscle memory carries
 *       across both tools.
 *    2. Suction-side / NPSH — atmospheric (altitude), vapour
 *       pressure (fluid temp), suction lift, suction friction.
 *    3. Pump duty point — flow + TDH + shaft + electrical kW,
 *       NPSHa, supplier-ready specification card, plus a what-if
 *       operating-curve table (friction scales as Q^1.852 with
 *       Hazen-Williams so the new pump head jumps non-linearly
 *       when the user upsizes flow).
 *    4. Solar (optional) — reveals the PV array simulator with
 *       pump kW, rated head, static and friction pre-filled.
 * --------------------------------------------------------------
 */

// Hazen-Williams C-factors — mirror the standalone friction-loss
// tool's MATERIALS list (same value codes so a user moving between
// the two tools sees identical material options).
const PIPE_MATERIALS = [
  { value: "150",  label: "HDPE / Polyethylene · C = 150" },
  { value: "150b", label: "PVC · C = 150" },
  { value: "140",  label: "Copper · C = 140" },
  { value: "130",  label: "Cast iron (cement-lined) · C = 130" },
  { value: "120",  label: "Galvanised steel · C = 120" },
  { value: "100",  label: "Steel (older / mild scale) · C = 100" },
  { value: "100c", label: "Concrete · C = 100" },
];
const C_OF = (v) => parseFloat((PIPE_MATERIALS.find((m) => m.value === v) || PIPE_MATERIALS[0]).value);

// Altitude → atmospheric pressure (m H₂O). 10.33 m at sea level,
// drops ~0.12 m per 100 m elevation in ZA's typical range.
function atmHeadAt(altitude_m) {
  const a = Math.max(0, altitude_m || 0);
  return 10.33 - 0.0012 * a;
}

// Saturation vapour pressure of water → m H₂O (Antoine fit, 0-100 °C).
// At 20 °C ≈ 0.24 m; 40 °C ≈ 0.75 m; 60 °C ≈ 2.03 m.
function vapHeadAt(temp_c) {
  const T = Math.max(0, Math.min(100, temp_c || 20));
  // Antoine: log10(P_mmHg) = A − B/(C + T), water (A=8.07, B=1730.6, C=233.4)
  const Pmm = Math.pow(10, 8.07131 - 1730.63 / (233.426 + T));
  const Pkpa = Pmm * 0.133322;
  return Pkpa / 9.80665;          // kPa → m of water
}

// -----------------------------------------------------------------------------
// Compact suffix-number input — keeps state in this file (avoids the global
// UnitNumber's full dimension system which is heavier than this tool needs).
// -----------------------------------------------------------------------------
function SuffixNum({ value, onChange, suffix, step = "any", min, testid }) {
  // Buffered text-mode input — fixes two UX bugs:
  //   (a) A field with value 2 wouldn't clear (controlled number input
  //       snapped empty → 0 → repaint as "0" before the user finished).
  //       We buffer the raw string locally so the field can sit empty.
  //   (b) "15 becomes 015":  parent state of 0 used to render as "0",
  //       then typing "15" produced "015". We treat 0 as the empty /
  //       placeholder state and never render a leading zero.
  const numeric = Number(value);
  const externalStr = Number.isFinite(numeric) && numeric !== 0 ? String(numeric) : "";
  const [buf, setBuf] = React.useState(externalStr);
  React.useEffect(() => {
    const parsed = parseFloat(buf.replace(",", "."));
    const same = Number.isFinite(parsed)
      ? Math.abs(parsed - numeric) < 1e-9
      : (buf === "" && numeric === 0);
    if (!same) setBuf(externalStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div className="flex items-stretch">
      <input
        type="text"
        inputMode="decimal"
        value={buf}
        placeholder="0"
        data-testid={testid}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw !== "" && !/^-?\d*[.,]?\d*$/.test(raw)) return;
          setBuf(raw);
          if (raw === "" || raw === "-" || raw === "." || raw === ",") { onChange(0); return; }
          const n = parseFloat(raw.replace(",", "."));
          if (Number.isFinite(n)) onChange(n);
        }}
        className="flex-1 min-w-0 px-3 py-2.5 bg-white border outline-none focus:border-[#1FB6C1]"
        style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40",
                 fontSize: "0.95rem", fontWeight: 500 }}
      />
      <span
        className="inline-flex items-center px-3 border border-l-0 font-mono"
        style={{ background: "#FAFAF7", borderColor: "rgba(12,42,64,0.16)", color: "rgba(12,42,64,0.65)", fontSize: "0.72rem" }}
      >
        {suffix}
      </span>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Pure computation (no JSX) for a segment — used by parent to roll up totals.
// State shape mirrors the Hazen-Williams advanced tool:
//   { material, idVal, idUnit, lenVal, lenUnit, flowVal, flowUnit, fittings }
// -----------------------------------------------------------------------------
function computeSegment(state) {
  const C = C_OF(state.material);
  const id_m = toBase("length_small", state.idVal || 0, state.idUnit) / 1000; // mm→m
  const pipe_len_m = toBase("length_big", state.lenVal || 0, state.lenUnit);
  const eqlen_m = fittingsEquivalentLength(state.fittings || {}, id_m);
  const total_len_m = pipe_len_m + eqlen_m;
  const flow_m3s = toBase("flow", state.flowVal || 0, state.flowUnit);
  const hL = hazenWilliamsHeadLoss({ flow_m3s, id_m, length_m: total_len_m, C });
  const v  = pipeVelocity({ flow_m3s, id_m });
  return {
    hL: hL || 0,
    v: v || 0,
    C,
    flow_m3s,
    pipe_len_m,
    eqlen_m,
    total_len_m,
  };
}

// Section heading — small navy bar with optional toggle.
function SectionHead({ children, badge, action }) {
  return (
    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
      <div className="font-mono uppercase"
        style={{ fontSize: "0.62rem", letterSpacing: "0.22em", color: "#0C2A40", fontWeight: 700 }}
      >
        {children}
        {badge && (
          <span className="ml-2 inline-block px-2 py-0.5"
            style={{ background: "rgba(31,182,193,0.10)", color: "#1FB6C1", borderRadius: 3, fontSize: "0.6rem", letterSpacing: "0.15em" }}
          >
            {badge}
          </span>
        )}
      </div>
      {action}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function PumpSizingWorkflow() {
  // ---------- 0. Mode toggle ----------
  // "design"   = forward (default existing behaviour) — pipes → friction → pump.
  // "existing" = reverse — characterise an existing system from a measured
  //              operating point (P_pump, P_end, current flow, static head)
  //              and re-spec the pump for a new target flow + end pressure.
  const [mode, setMode] = useState("design");

  // ---------- 1. System hydraulics ----------
  // Segment shape mirrors the standalone Hazen-Williams advanced tool:
  //   { material, idVal, idUnit, lenVal, lenUnit, flowVal, flowUnit, fittings }
  // Defaults match a typical mainline / sub-main / lateral for a small lodge.
  const [mainline, setMainline] = useState({
    material: "150",
    idVal: 75,  idUnit: "mm",
    lenVal: 100, lenUnit: "m",
    flowVal: 200, flowUnit: "Lpm",
    fittings: {},
  });
  const [submainOpen, setSubmainOpen] = useState(false);
  const [submain, setSubmain] = useState({
    material: "150",
    idVal: 50,  idUnit: "mm",
    lenVal: 40,  lenUnit: "m",
    flowVal: 100, flowUnit: "Lpm",
    fittings: {},
  });
  const [lateralOpen, setLateralOpen] = useState(false);
  const [lateral, setLateral] = useState({
    material: "150",
    idVal: 25,  idUnit: "mm",
    lenVal: 30,  lenUnit: "m",
    flowVal: 50,  flowUnit: "Lpm",
    fittings: {},
  });

  const [staticM, setStaticM] = useState(20);   // static lift + outlet pressure (m)
  const [safetyPct, setSafetyPct] = useState(10);

  const mainCalc = useMemo(() => computeSegment(mainline), [mainline]);
  const subCalc  = useMemo(() => computeSegment(submain),  [submain]);
  const latCalc  = useMemo(() => computeSegment(lateral),  [lateral]);

  // Friction is the critical path: mainline always, sub-main if open, lateral if open.
  const frictionTotal = mainCalc.hL
                      + (submainOpen ? subCalc.hL : 0)
                      + (lateralOpen ? latCalc.hL : 0);

  // Design flow = mainline flow (worst-case feed coming into the network).
  const designFlow_m3s  = mainCalc.flow_m3s;
  const designFlowLpm   = fromBase("flow", designFlow_m3s, "Lpm");
  const designFlow_m3h  = fromBase("flow", designFlow_m3s, "m3h");

  // TDH (before margin) = static + total friction. Margin layered on top.
  const tdhRaw   = staticM + frictionTotal;
  const tdhFinal = tdhRaw * (1 + safetyPct / 100);

  // ---------- 2. NPSH ----------
  const [altitudeM,        setAltitudeM]        = useState(0);
  const [fluidTempC,       setFluidTempC]       = useState(20);
  const [suctionLiftM,     setSuctionLiftM]     = useState(2);   // positive = below pump centreline (lift up)
  const [suctionFrictionM, setSuctionFrictionM] = useState(0.5);
  const [npshrM,           setNpshrM]           = useState(3);   // pump nameplate NPSHr — user fills if known

  const atmM = atmHeadAt(altitudeM);
  const vapM = vapHeadAt(fluidTempC);
  // Sign convention: suctionLiftM is the height the pump must LIFT water,
  // so it subtracts from atm. A flooded suction (negative lift) would add.
  const npshA = npshAvailable({ atm_m: atmM, vap_m: vapM, static_m: -suctionLiftM, friction_m: suctionFrictionM });
  const npshMargin = npshA - npshrM;

  // ---------- 3. Pump duty point ----------
  const [pumpEffPct, setPumpEffPct] = useState(70);
  const [motorEffPct, setMotorEffPct] = useState(92);

  // Hydraulic kW = ρ × g × Q × H ÷ 1000 (Q m³/s, H m, ρ=1000 kg/m³, g=9.81)
  const hydraulicKw  = 1000 * 9.81 * designFlow_m3s * tdhFinal / 1000;
  const shaftKw      = pumpEffPct > 0 ? hydraulicKw / (pumpEffPct / 100) : 0;
  const electricalKw = motorEffPct > 0 ? shaftKw / (motorEffPct / 100) : 0;

  // Suggest the next standard IEC motor size ≥ shaft kW.
  const recommendedMotorKw = useMemo(() => {
    const need = shaftKw;
    return PUMP_SIZES_KW.find((k) => k >= need) || PUMP_SIZES_KW[PUMP_SIZES_KW.length - 1];
  }, [shaftKw]);

  // ---------- 4. Solar (optional) ----------
  const [solarOpen, setSolarOpen] = useState(false);
  const [regionId, setRegionId] = useState("nelspruit");
  const region = PV_REGIONS.find((r) => r.id === regionId) || PV_REGIONS[0];
  const [panelW, setPanelW] = useState(550);
  // Allow the user to override pump duty inputs for the solar sim, but seed
  // from the workflow's computed values.
  const [solarOverride, setSolarOverride] = useState(false);
  const [overridePumpKw, setOverridePumpKw] = useState(recommendedMotorKw);
  const [overrideQratedLpm, setOverrideQratedLpm] = useState(designFlowLpm);
  const [overrideHTdh, setOverrideHTdh] = useState(tdhFinal);
  const solarPumpKw     = solarOverride ? overridePumpKw     : recommendedMotorKw;
  const solarQratedLpm  = solarOverride ? overrideQratedLpm  : designFlowLpm;
  const solarTdh        = solarOverride ? overrideHTdh        : tdhFinal;

  const [pvPanels, setPvPanels] = useState(8);
  const solarSimWinter = useMemo(() => simulateSolarPumpDay({
    pumpKw: solarPumpKw,
    qRatedLpm: solarQratedLpm,
    qAt30Lpm: solarQratedLpm * 0.55,
    arrayKwp: (pvPanels * panelW) / 1000,
    psh: region.pshWinter,
    daylightHours: DAYLIGHT_HOURS.winter,
    etaSystem: 0.75,
    hRatedM: solarTdh,
    hStaticM: staticM,
    hFrictionRatedM: frictionTotal * (1 + safetyPct / 100),
  }), [solarPumpKw, solarQratedLpm, pvPanels, panelW, region, solarTdh, staticM, frictionTotal, safetyPct]);

  const solarSimAnnual = useMemo(() => simulateSolarPumpDay({
    pumpKw: solarPumpKw,
    qRatedLpm: solarQratedLpm,
    qAt30Lpm: solarQratedLpm * 0.55,
    arrayKwp: (pvPanels * panelW) / 1000,
    psh: region.pshAnnual,
    daylightHours: DAYLIGHT_HOURS.annual,
    etaSystem: 0.75,
    hRatedM: solarTdh,
    hStaticM: staticM,
    hFrictionRatedM: frictionTotal * (1 + safetyPct / 100),
  }), [solarPumpKw, solarQratedLpm, pvPanels, panelW, region, solarTdh, staticM, frictionTotal, safetyPct]);

  // ---------- 5. What-if operating curve ----------
  // Hazen-Williams: friction scales as Q^1.852, so when the user upsizes
  // the design flow the new TDH jumps non-linearly.  The table below
  // anchors at the current design flow (1.0×) and shows friction, TDH,
  // discharge bar, hydraulic kW and the recommended IEC motor size at
  // 0.5× → 2.0× of the design point.  When the user bumps the design
  // flow the table re-anchors automatically.
  const whatIfRatios = [0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0];
  const whatIfRows = useMemo(() => whatIfRatios.map((r) => {
    const newFriction = frictionTotal * Math.pow(r, 1.852);
    const newTdh = (staticM + newFriction) * (1 + safetyPct / 100);
    const newFlow_m3h = designFlow_m3h * r;
    const newFlow_m3s = designFlow_m3s * r;
    const newHydraulicKw = 1000 * 9.81 * newFlow_m3s * newTdh / 1000;
    const newShaftKw = pumpEffPct > 0 ? newHydraulicKw / (pumpEffPct / 100) : 0;
    const newMotor = PUMP_SIZES_KW.find((k) => k >= newShaftKw) || PUMP_SIZES_KW[PUMP_SIZES_KW.length - 1];
    return {
      ratio: r,
      flow_m3h: newFlow_m3h,
      flow_Lpm: newFlow_m3h * 1000 / 60,
      friction_m: newFriction,
      tdh_m: newTdh,
      tdh_bar: newTdh * 0.0980665,
      hydKw: newHydraulicKw,
      shaftKw: newShaftKw,
      motorKw: newMotor,
    };
  }), [frictionTotal, staticM, safetyPct, designFlow_m3h, designFlow_m3s, pumpEffPct]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- 6. Existing-system reverse mode ----------
  // Operator measures pump discharge pressure, end-of-line pressure and
  // the current flow rate.  We back-solve the system constant K from
  //   friction_m = K · Q^1.852         (Hazen-Williams scaling law)
  // then forecast the new pump duty at a user-supplied new flow + new
  // end-of-line pressure.
  const [ex_pumpBar,        setEx_pumpBar]        = useState(6);
  const [ex_endBar,         setEx_endBar]         = useState(4);
  const [ex_flow_m3h,       setEx_flow_m3h]       = useState(4);
  const [ex_staticM,        setEx_staticM]        = useState(0);
  const [ex_newFlow_m3h,    setEx_newFlow_m3h]    = useState(6);
  const [ex_newEndBar,      setEx_newEndBar]      = useState(4);
  const [ex_safetyPct,      setEx_safetyPct]      = useState(10);
  const [ex_pumpEffPct,     setEx_pumpEffPct]     = useState(70);
  const [ex_motorEffPct,    setEx_motorEffPct]    = useState(92);

  const BAR_TO_M = 10.197162; // 1 bar of water column = 10.197 m

  // Friction at the measured operating point.
  const ex_currentFriction_m = Math.max(0,
    (ex_pumpBar - ex_endBar) * BAR_TO_M - ex_staticM);
  // System constant K from hL = K · Q^1.852  (Q in m³/h, hL in m).
  const ex_K = ex_flow_m3h > 0 && ex_currentFriction_m > 0
    ? ex_currentFriction_m / Math.pow(ex_flow_m3h, 1.852)
    : null;
  // Forecast at the new flow.
  const ex_newFriction_m = ex_K !== null
    ? ex_K * Math.pow(Math.max(0, ex_newFlow_m3h), 1.852)
    : null;
  const ex_newEnd_m = ex_newEndBar * BAR_TO_M;
  // New TDH = static + new friction + new end pressure, with safety margin.
  const ex_newTdh_m_raw = ex_newFriction_m !== null
    ? ex_staticM + ex_newFriction_m + ex_newEnd_m
    : null;
  const ex_newTdh_m = ex_newTdh_m_raw !== null
    ? ex_newTdh_m_raw * (1 + ex_safetyPct / 100)
    : null;
  const ex_newPumpBar = ex_newTdh_m !== null ? ex_newTdh_m / BAR_TO_M : null;
  const ex_newFlow_m3s = (Number(ex_newFlow_m3h) || 0) / 3600;
  const ex_newHydKw = ex_newTdh_m !== null
    ? 1000 * 9.81 * ex_newFlow_m3s * ex_newTdh_m / 1000
    : null;
  const ex_newShaftKw = ex_newHydKw !== null && ex_pumpEffPct > 0
    ? ex_newHydKw / (ex_pumpEffPct / 100) : null;
  const ex_newElecKw = ex_newShaftKw !== null && ex_motorEffPct > 0
    ? ex_newShaftKw / (ex_motorEffPct / 100) : null;
  const ex_newMotorKw = ex_newShaftKw !== null
    ? (PUMP_SIZES_KW.find((k) => k >= ex_newShaftKw) || PUMP_SIZES_KW[PUMP_SIZES_KW.length - 1])
    : null;
  // Verdict text for the reverse-mode spec card.
  const ex_status = ex_K === null ? "warn"
    : ex_newPumpBar !== null && ex_newPumpBar > ex_pumpBar * 2 ? "alert"
    : ex_newPumpBar !== null && ex_newPumpBar > ex_pumpBar ? "warn"
    : "ok";

  // =============================================================================
  return (
    <ToolShell
      slug="pump-sizing"
      title="Pump sizing — full duty-point workflow"
      oneLiner="One workspace. Hydraulics → NPSH → duty point → optional solar PV — without retyping a single value. Or flip to existing-system mode to back-solve a pump spec from a measured site reading. Produces a supplier-ready specification."
      onExport={() => mode === "design" ? ({
        inputs: [
          { label: "Calculation mode", value: "Design new system" },
          { label: "── System hydraulics ──", value: "" },
          { label: "Mainline",
            value: `${mainline.idVal} ${UNITS.length_small.labels[mainline.idUnit]} ID · ${mainline.lenVal} ${UNITS.length_big.labels[mainline.lenUnit]} · ${mainline.flowVal} ${UNITS.flow.labels[mainline.flowUnit]} · ${PIPE_MATERIALS.find((m) => m.value === mainline.material)?.label}` },
          ...fittingsSummary(mainline.fittings || {}).map((f) => ({ label: `· Mainline fitting`, value: `${f.label} × ${f.qty}` })),
          ...(submainOpen ? [{ label: "Sub-main",
            value: `${submain.idVal} ${UNITS.length_small.labels[submain.idUnit]} ID · ${submain.lenVal} ${UNITS.length_big.labels[submain.lenUnit]} · ${submain.flowVal} ${UNITS.flow.labels[submain.flowUnit]}` }] : []),
          ...(submainOpen ? fittingsSummary(submain.fittings || {}).map((f) => ({ label: `· Sub-main fitting`, value: `${f.label} × ${f.qty}` })) : []),
          ...(lateralOpen ? [{ label: "Lateral",
            value: `${lateral.idVal} ${UNITS.length_small.labels[lateral.idUnit]} ID · ${lateral.lenVal} ${UNITS.length_big.labels[lateral.lenUnit]} · ${lateral.flowVal} ${UNITS.flow.labels[lateral.flowUnit]}` }] : []),
          ...(lateralOpen ? fittingsSummary(lateral.fittings || {}).map((f) => ({ label: `· Lateral fitting`, value: `${f.label} × ${f.qty}` })) : []),
          { label: "Static head (lift + outlet pressure)", value: staticM, unit: "m" },
          { label: "Safety margin", value: safetyPct, unit: "%" },
          { label: "── NPSH context ──", value: "" },
          { label: "Site altitude", value: altitudeM, unit: "m" },
          { label: "Fluid temperature", value: fluidTempC, unit: "°C" },
          { label: "Suction lift (water level → pump centreline)", value: suctionLiftM, unit: "m" },
          { label: "Suction-line friction", value: suctionFrictionM, unit: "m" },
          { label: "Pump nameplate NPSHr (if known)", value: npshrM, unit: "m" },
          { label: "── Efficiency ──", value: "" },
          { label: "Pump efficiency", value: pumpEffPct, unit: "%" },
          { label: "Motor efficiency", value: motorEffPct, unit: "%" },
        ],
        results: [
          { label: "Mainline friction", value: fmt(mainCalc.hL, 2), unit: "m" },
          ...(mainCalc.eqlen_m > 0 ? [{ label: "Mainline fittings equiv. length", value: fmt(mainCalc.eqlen_m, 1), unit: "m of pipe" }] : []),
          ...(submainOpen ? [{ label: "Sub-main friction", value: fmt(subCalc.hL, 2), unit: "m" }] : []),
          ...(submainOpen && subCalc.eqlen_m > 0 ? [{ label: "Sub-main fittings equiv. length", value: fmt(subCalc.eqlen_m, 1), unit: "m of pipe" }] : []),
          ...(lateralOpen ? [{ label: "Lateral friction", value: fmt(latCalc.hL, 2), unit: "m" }] : []),
          ...(lateralOpen && latCalc.eqlen_m > 0 ? [{ label: "Lateral fittings equiv. length", value: fmt(latCalc.eqlen_m, 1), unit: "m of pipe" }] : []),
          { label: "Total friction (critical path)", value: fmt(frictionTotal, 2), unit: "m" },
          { label: "TDH (with safety margin)", value: fmt(tdhFinal, 1), unit: "m",
            alts: [{ value: fmt(tdhFinal * 0.0980665, 2), unit: "bar" }] },
          { label: "Design flow", value: fmt(designFlow_m3h, 2), unit: "m³/h",
            alts: [{ value: fmt(designFlowLpm, 0), unit: "L/min" }] },
          { label: "NPSHa at this site", value: fmt(npshA, 2), unit: "m" },
          { label: "NPSH margin (NPSHa − NPSHr)", value: fmt(npshMargin, 2), unit: "m",
            note: npshMargin < 0.5 ? "Below 0.5 m — cavitation risk. Increase suction-side ID, reduce lift, or specify a pump with lower NPSHr." : null },
          { label: "Hydraulic power", value: fmt(hydraulicKw, 2), unit: "kW" },
          { label: "Shaft power", value: fmt(shaftKw, 2), unit: "kW" },
          { label: "Electrical input", value: fmt(electricalKw, 2), unit: "kW" },
          { label: "Recommended motor size (next standard IEC)", value: recommendedMotorKw, unit: "kW" },
          { label: "── What-if operating curve (Q ∝ Q^1.852 friction) ──", value: "" },
          ...whatIfRows.map((r) => ({
            label: `${r.ratio.toFixed(2)}× design flow · ${fmt(r.flow_m3h, 2)} m³/h`,
            value: `TDH ${fmt(r.tdh_m, 1)} m (${fmt(r.tdh_bar, 2)} bar)`,
            unit: `· ${fmt(r.shaftKw, 2)} kW shaft · ${r.motorKw} kW motor`,
          })),
          ...(solarOpen ? [
            { label: "── Solar PV summary ──", value: "" },
            { label: "Site", value: `${region.name} · ${region.region}` },
            { label: "Array size", value: `${pvPanels} × ${panelW} W`, unit: `= ${fmt(pvPanels * panelW / 1000, 2)} kWp` },
            { label: "Winter delivery", value: fmt(solarSimWinter.volumeLitres, 0), unit: "L/day" },
            { label: "Annual avg delivery", value: fmt(solarSimAnnual.volumeLitres, 0), unit: "L/day" },
          ] : []),
        ],
        notes: [
          "Friction by Hazen-Williams hL = 10.67·L·Q^1.852 / (C^1.852·D^4.87). Critical path only (single linear route to the worst-case emitter).",
          "Fittings handled via Crane-TP410 equivalent-length-of-pipe (added to L before the formula runs).",
          "TDH = static + Σ critical-path friction × (1 + safety margin).",
          "What-if curve: friction scales as Q^1.852 — doubling the flow roughly quadruples the friction component of TDH.",
          "Hydraulic kW = ρ·g·Q·H / 1000. Shaft kW = hydraulic ÷ pump efficiency. Electrical kW = shaft ÷ motor efficiency.",
          "NPSHa = atmospheric − vapour pressure − suction lift − suction friction. Choose a pump with NPSHr at least 0.5 m below NPSHa.",
          "Reference only — the HidroVerse team will validate against the actual pump curve, motor selection and supplier-specific NPSHr.",
        ],
        spec: designFlow_m3h > 0 && tdhFinal > 0 ? {
          title: "Supplier-ready pump specification",
          body: `Specify a pump rated for <strong>${fmt(designFlow_m3h, 2)} m³/h</strong> at <strong>${fmt(tdhFinal, 1)} m TDH</strong> (<strong>${fmt(tdhFinal * 0.0980665, 2)} bar</strong>), driven by a <strong>${recommendedMotorKw} kW</strong> motor. Required NPSHr ≤ <strong>${fmt(Math.max(0, npshA - 0.5), 1)} m</strong> at site altitude <strong>${altitudeM} m</strong>, fluid temperature <strong>${fluidTempC} °C</strong>. Pump efficiency assumed <strong>${pumpEffPct} %</strong>, motor efficiency <strong>${motorEffPct} %</strong>. Critical-path friction <strong>${fmt(frictionTotal, 2)} m</strong>${(mainCalc.eqlen_m + (submainOpen ? subCalc.eqlen_m : 0) + (lateralOpen ? latCalc.eqlen_m : 0)) > 0 ? ` (incl. <strong>${fmt(mainCalc.eqlen_m + (submainOpen ? subCalc.eqlen_m : 0) + (lateralOpen ? latCalc.eqlen_m : 0), 1)} m</strong> equivalent length from fittings)` : ""}, static head <strong>${staticM} m</strong>, safety margin <strong>${safetyPct} %</strong>.`,
        } : null,
      }) : ({
        // ───── REVERSE — Existing-system mode ─────
        inputs: [
          { label: "Calculation mode", value: "Specify pump for existing system" },
          { label: "── Measured operating point ──", value: "" },
          { label: "Current pump discharge pressure", value: ex_pumpBar, unit: "bar" },
          { label: "Current end-of-line pressure", value: ex_endBar, unit: "bar" },
          { label: "Current flow", value: ex_flow_m3h, unit: "m³/h" },
          { label: "Static head (lift + outlet)", value: ex_staticM, unit: "m" },
          { label: "── New target ──", value: "" },
          { label: "Desired new flow", value: ex_newFlow_m3h, unit: "m³/h" },
          { label: "Desired new end-of-line pressure", value: ex_newEndBar, unit: "bar" },
          { label: "Safety margin", value: ex_safetyPct, unit: "%" },
          { label: "── Efficiency ──", value: "" },
          { label: "Pump efficiency", value: ex_pumpEffPct, unit: "%" },
          { label: "Motor efficiency", value: ex_motorEffPct, unit: "%" },
        ],
        results: [
          { label: "Current friction loss across system",
            value: fmt(ex_currentFriction_m, 2), unit: "m",
            alts: [{ value: fmt(ex_currentFriction_m * 0.0980665, 2), unit: "bar" }],
            note: "(P_pump − P_end) × 10.197 m/bar − static head." },
          { label: "System constant K (friction = K · Q^1.852)",
            value: ex_K !== null ? ex_K.toExponential(3) : "—",
            note: "Back-solved from the measured operating point. Stays constant for the same pipework." },
          { label: "Forecast friction at new flow",
            value: fmt(ex_newFriction_m, 2), unit: "m",
            alts: ex_newFriction_m !== null ? [{ value: fmt(ex_newFriction_m * 0.0980665, 2), unit: "bar" }] : null,
            note: `Friction scales as (Q_new / Q_now)^1.852 = ${ex_flow_m3h > 0 ? fmt(Math.pow(ex_newFlow_m3h / ex_flow_m3h, 1.852), 3) : "—"}×.` },
          { label: "Required new TDH (with safety margin)",
            value: fmt(ex_newTdh_m, 1), unit: "m" },
          { label: "Required new pump discharge pressure",
            value: fmt(ex_newPumpBar, 2), unit: "bar",
            status: ex_status, note: ex_newPumpBar !== null
              ? `Up from ${fmt(ex_pumpBar, 2)} bar today — pressure jump ${fmt(ex_newPumpBar - ex_pumpBar, 2)} bar.`
              : null },
          { label: "Hydraulic power", value: fmt(ex_newHydKw, 2), unit: "kW" },
          { label: "Shaft power", value: fmt(ex_newShaftKw, 2), unit: "kW" },
          { label: "Electrical input", value: fmt(ex_newElecKw, 2), unit: "kW" },
          { label: "Recommended motor size (next standard IEC)", value: ex_newMotorKw || "—", unit: "kW" },
        ],
        notes: [
          "Step 1 — derive current friction: f_now = (P_pump − P_end) × 10.197 − static.",
          "Step 2 — back-solve system constant K from Hazen-Williams: K = f_now / Q_now^1.852.",
          "Step 3 — forecast at new flow: f_new = K · Q_new^1.852.",
          "Step 4 — new TDH = static + f_new + (P_end_new × 10.197), × (1 + safety margin).",
          "Step 5 — new pump discharge = TDH_new ÷ 10.197.",
          "Reference only — assumes the same pipework, no major leaks and the original pump was at its rated efficiency point. HidroVerse will validate against actual pump curve and field measurements.",
        ],
        spec: ex_newPumpBar !== null && ex_K !== null ? {
          title: "Pump retrofit specification",
          body: `Existing system: pump at <strong>${fmt(ex_pumpBar, 2)} bar</strong>, end-of-line at <strong>${fmt(ex_endBar, 2)} bar</strong>, flow <strong>${fmt(ex_flow_m3h, 2)} m³/h</strong>, static <strong>${ex_staticM} m</strong> ⇒ current friction <strong>${fmt(ex_currentFriction_m, 2)} m</strong> (<strong>${fmt(ex_currentFriction_m * 0.0980665, 2)} bar</strong>). To deliver <strong>${fmt(ex_newFlow_m3h, 2)} m³/h</strong> at <strong>${fmt(ex_newEndBar, 2)} bar</strong> end-of-line, friction climbs to <strong>${fmt(ex_newFriction_m, 2)} m</strong> (Q^1.852 scaling). New pump must produce <strong>${fmt(ex_newFlow_m3h, 2)} m³/h at ${fmt(ex_newPumpBar, 2)} bar discharge</strong> (<strong>${fmt(ex_newTdh_m, 1)} m TDH</strong>) — <strong>${ex_newMotorKw} kW</strong> IEC motor at <strong>${ex_pumpEffPct} %</strong> pump / <strong>${ex_motorEffPct} %</strong> motor efficiency.`,
        } : null,
      })}
    >
      {/* INTRO BANNER */}
      <div className="mb-5 p-4"
        style={{ background: "#F3FBFB", border: "1px solid rgba(31,182,193,0.30)" }}
        data-testid="ps-intro-banner"
      >
        <p className="font-light leading-relaxed" style={{ fontSize: "0.84rem", color: "#0C2A40" }}>
          {mode === "design"
            ? <>This workflow stitches the friction-loss, NPSH and pump-power calculators
                into one workspace. Start with the mainline pipe — for most pump-house
                installations that&apos;s all you need. Expand the sub-main / lateral sections
                only if your network has those tiers. Each segment mirrors the standalone
                Hazen-Williams advanced tool so muscle memory carries across. Everything
                downstream recomputes live, and the final Export PDF gives you a
                supplier-ready specification sheet.</>
            : <>Got an existing system that&apos;s under-performing? Plug in the readings
                you measured on site — pump discharge pressure, end-of-line pressure,
                current flow, static head — and the tool back-solves the system constant.
                Then tell it the new flow you need and it forecasts the new pump duty
                using Hazen-Williams Q^1.852 scaling. The Export PDF gives you a
                retrofit specification ready for the supplier.</>}
        </p>
      </div>

      {/* ───────── Calculation mode toggle ─────────
          Two-pill toggle: forward design ↔ reverse (existing-system) retrofit. */}
      <div
        className="mb-5 p-1 inline-flex items-stretch"
        data-testid="ps-mode-toggle"
        style={{
          background: "#F4F2EC",
          border: "1px solid rgba(12,42,64,0.10)",
          borderRadius: 999,
          gap: 2,
        }}
      >
        {[
          { v: "design",   label: "Design new system",            hint: "Forward · pipes → friction → pump" },
          { v: "existing", label: "Specify pump for existing system", hint: "Reverse · back-solve from measured readings" },
        ].map((opt) => {
          const active = mode === opt.v;
          return (
            <button
              key={opt.v}
              type="button"
              onClick={() => setMode(opt.v)}
              data-testid={`ps-mode-${opt.v}`}
              title={opt.hint}
              style={{
                padding: "8px 16px",
                background: active ? "#0C2A40" : "transparent",
                color: active ? "#FFFFFF" : "rgba(12,42,64,0.7)",
                fontSize: "0.78rem",
                fontWeight: active ? 700 : 500,
                letterSpacing: "0.02em",
                border: "none",
                borderRadius: 999,
                cursor: "pointer",
                transition: "all 0.18s ease",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {mode === "design" && (<>
      {/* ─────────────────────────────────────────────────────────── */}
      {/* SECTION 1 — SYSTEM HYDRAULICS                                */}
      {/* ─────────────────────────────────────────────────────────── */}
      <SectionHead>1 · System hydraulics</SectionHead>

      <div className="p-5 mb-3"
        style={{ background: "#FFFFFF", border: "1px solid rgba(12,42,64,0.08)", borderLeft: "3px solid #0C2A40" }}
      >
        <div className="font-mono uppercase mb-3"
          style={{ fontSize: "0.6rem", letterSpacing: "0.2em", color: "#0C2A40", fontWeight: 700 }}>
          Mainline · pump → discharge
        </div>
        <PipeSegmentLive state={mainline} setState={setMainline} testidPrefix="ps-main" />
      </div>

      {/* Sub-main accordion */}
      <div className="mb-3" style={{ background: "#FFFFFF", border: "1px solid rgba(12,42,64,0.08)", borderLeft: "3px solid #1FB6C1" }}>
        <button
          type="button"
          onClick={() => setSubmainOpen((v) => !v)}
          data-testid="ps-submain-toggle"
          className="w-full px-5 py-3 flex items-center justify-between"
          style={{ background: submainOpen ? "rgba(31,182,193,0.06)" : "#FFFFFF" }}
        >
          <span className="font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.2em", color: "#1FB6C1", fontWeight: 700 }}>
            Sub-main · manifold → zone valve {submainOpen ? "" : "(optional)"}
          </span>
          <span className="font-mono" style={{ fontSize: "0.7rem", color: "rgba(12,42,64,0.55)" }}>
            {submainOpen ? "▾ Open" : "▸ Add sub-main"}
          </span>
        </button>
        {submainOpen && (
          <div className="p-5 pt-3" data-testid="ps-submain-panel">
            <PipeSegmentLive state={submain} setState={setSubmain} testidPrefix="ps-sub" />
          </div>
        )}
      </div>

      {/* Lateral accordion */}
      <div className="mb-4" style={{ background: "#FFFFFF", border: "1px solid rgba(12,42,64,0.08)", borderLeft: "3px solid #3A8FB0" }}>
        <button
          type="button"
          onClick={() => setLateralOpen((v) => !v)}
          data-testid="ps-lateral-toggle"
          className="w-full px-5 py-3 flex items-center justify-between"
          style={{ background: lateralOpen ? "rgba(58,143,176,0.06)" : "#FFFFFF" }}
        >
          <span className="font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.2em", color: "#3A8FB0", fontWeight: 700 }}>
            Lateral · zone valve → emitters {lateralOpen ? "" : "(optional)"}
          </span>
          <span className="font-mono" style={{ fontSize: "0.7rem", color: "rgba(12,42,64,0.55)" }}>
            {lateralOpen ? "▾ Open" : "▸ Add lateral"}
          </span>
        </button>
        {lateralOpen && (
          <div className="p-5 pt-3" data-testid="ps-lateral-panel">
            <PipeSegmentLive state={lateral} setState={setLateral} testidPrefix="ps-lat" />
          </div>
        )}
      </div>

      {/* Static + safety margin */}
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <Field label="Static head" hint="Vertical lift from water-source level up to the highest discharge point. Add outlet pressure if any (1 bar ≈ 10 m).">
          <SuffixNum value={staticM} onChange={setStaticM} suffix="m" step="1" min={0} testid="ps-static" />
        </Field>
        <Field label="Pump safety margin" hint="Typical 10–15 % over the calculated TDH. Accounts for pipe ageing, fitting losses, and minor flow excursions.">
          <SuffixNum value={safetyPct} onChange={setSafetyPct} suffix="%" step="1" min={0} testid="ps-safety" />
        </Field>
      </div>

      {/* TDH summary */}
      <div className="mt-4 px-5 py-4 flex flex-wrap items-baseline gap-x-2 gap-y-1"
        style={{ background: "#F3FBFB", border: "1px solid rgba(31,182,193,0.30)" }}
        data-testid="ps-tdh-summary"
      >
        <div className="font-mono uppercase mr-3" style={{ fontSize: "0.58rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.55)" }}>
          Total dynamic head
        </div>
        <span style={{ fontSize: "0.92rem", color: "#0C2A40" }}>static {fmt(staticM, 1)} m</span>
        <span className="font-mono" style={{ fontSize: "0.8rem", color: "rgba(12,42,64,0.55)" }}>+</span>
        <span style={{ fontSize: "0.92rem", color: "#0C2A40" }}>friction {fmt(frictionTotal, 2)} m</span>
        <span className="font-mono" style={{ fontSize: "0.8rem", color: "rgba(12,42,64,0.55)" }}>+</span>
        <span style={{ fontSize: "0.92rem", color: "#0C2A40" }}>safety {safetyPct}%</span>
        <span className="font-mono" style={{ fontSize: "0.8rem", color: "rgba(12,42,64,0.55)" }}>=</span>
        <span style={{ fontSize: "1.2rem", fontWeight: 700, color: "#0C2A40" }}>{fmt(tdhFinal, 1)} m</span>
        <span className="font-mono ml-auto" style={{ fontSize: "0.7rem", color: "rgba(12,42,64,0.55)" }}>
          at design flow {fmt(designFlow_m3h, 2)} m³/h ({fmt(designFlowLpm, 0)} L/min)
        </span>
      </div>

      {/* ─────────────────────────────────────────────────────────── */}
      {/* SECTION 2 — NPSH                                             */}
      {/* ─────────────────────────────────────────────────────────── */}
      <div className="mt-8">
        <SectionHead>2 · Suction-side / NPSH</SectionHead>
        <p className="font-light leading-relaxed mb-3" style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.65)" }}>
          Net Positive Suction Head Available (NPSHa) is the pressure the fluid
          arrives at the pump&apos;s eye with — set by your site geometry, altitude
          and fluid temperature. The pump&apos;s nameplate NPSHr is the minimum it
          needs. NPSHa &lt; NPSHr → cavitation, noise, impeller damage.
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Site altitude" hint="Higher altitude → lower atmospheric pressure → less NPSHa. Nelspruit ≈ 670 m, Johannesburg ≈ 1750 m.">
            <SuffixNum value={altitudeM} onChange={setAltitudeM} suffix="m above sea" step="50" min={0} testid="ps-alt" />
          </Field>
          <Field label="Fluid temperature" hint="Higher temp → higher vapour pressure → less NPSHa. Below 30 °C the effect is small; from 50 °C up it matters.">
            <SuffixNum value={fluidTempC} onChange={setFluidTempC} suffix="°C" step="1" min={0} testid="ps-temp" />
          </Field>
          <Field label="Suction lift" hint="Vertical distance from the water surface UP to the pump centreline. Use 0 for a flooded suction, negative if the source sits above the pump.">
            <SuffixNum value={suctionLiftM} onChange={setSuctionLiftM} suffix="m lift" step="0.5" testid="ps-lift" />
          </Field>
          <Field label="Suction-line friction" hint="Friction loss between source and pump intake (typically 0.3–1.5 m for a well-designed suction).">
            <SuffixNum value={suctionFrictionM} onChange={setSuctionFrictionM} suffix="m" step="0.1" min={0} testid="ps-sfric" />
          </Field>
          <Field label="Pump nameplate NPSHr" hint="Take this from the pump's published curve at your duty flow. Typical 1–4 m for vertical multistage pumps. Leave as default if not known yet.">
            <SuffixNum value={npshrM} onChange={setNpshrM} suffix="m" step="0.5" min={0} testid="ps-npshr" />
          </Field>
        </div>

        <div className="grid md:grid-cols-3 gap-3 mt-4">
          <NpshStat label="Atmospheric head" value={atmM} unit="m" detail={`at ${altitudeM} m altitude`} testid="ps-atm" />
          <NpshStat label="Vapour pressure head" value={vapM} unit="m" detail={`at ${fluidTempC} °C`} testid="ps-vap" />
          <NpshStat label="NPSHa at this site" value={npshA} unit="m" detail="atm − vapour − lift − friction" highlight testid="ps-npsha" />
        </div>

        <div className="mt-3 px-4 py-3"
          style={{
            background: npshMargin >= 0.5 ? "#F0F9EE" : "#FFF5EE",
            border: `1px solid ${npshMargin >= 0.5 ? "rgba(91,136,71,0.30)" : "rgba(184,92,56,0.40)"}`,
          }}
          data-testid="ps-npsh-verdict"
        >
          <div className="font-mono uppercase mb-1"
            style={{ fontSize: "0.6rem", letterSpacing: "0.2em", color: npshMargin >= 0.5 ? "#5B8847" : "#B85C38", fontWeight: 700 }}>
            NPSH margin = {fmt(npshMargin, 2)} m
          </div>
          <p className="font-light leading-snug" style={{ fontSize: "0.78rem", color: "#0C2A40" }}>
            {npshMargin >= 0.5
              ? `NPSHa (${fmt(npshA, 2)} m) exceeds NPSHr (${fmt(npshrM, 2)} m) by ${fmt(npshMargin, 2)} m — safe operating margin. Any pump with NPSHr ≤ ${fmt(npshA - 0.5, 1)} m will work here.`
              : `NPSHa (${fmt(npshA, 2)} m) is too close to (or below) NPSHr (${fmt(npshrM, 2)} m). Cavitation risk. Lower the pump, shorten the suction line, increase suction ID, or specify a pump with lower NPSHr.`}
          </p>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────── */}
      {/* SECTION 3 — PUMP DUTY POINT                                  */}
      {/* ─────────────────────────────────────────────────────────── */}
      <div className="mt-8">
        <SectionHead badge="Compiled output">3 · Pump duty point</SectionHead>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <Field label="Pump efficiency" hint="The pump's hydraulic-to-shaft efficiency at the duty point. 65–75 % is typical for vertical multistage at BEP; positive-displacement pumps can hit 85 %.">
            <SuffixNum value={pumpEffPct} onChange={setPumpEffPct} suffix="%" step="1" min={1} testid="ps-pumpeff" />
          </Field>
          <Field label="Motor efficiency" hint="IE3 / IE4 motors are 90–95 % in their efficient operating band. 92 % is a safe default for a 1.1–22 kW IE3 motor.">
            <SuffixNum value={motorEffPct} onChange={setMotorEffPct} suffix="%" step="1" min={1} testid="ps-motoreff" />
          </Field>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          <DutyStat label="Design flow"     value={fmt(designFlow_m3h, 2)} unit="m³/h"  alt={`${fmt(designFlowLpm, 0)} L/min`} testid="ps-duty-flow" />
          <DutyStat label="TDH"              value={fmt(tdhFinal, 1)}       unit="m"     alt={`pre-margin ${fmt(tdhRaw, 1)} m`} testid="ps-duty-tdh" />
          <DutyStat label="Hydraulic power" value={fmt(hydraulicKw, 2)}    unit="kW"    alt="ρ·g·Q·H ÷ 1000" testid="ps-duty-hyd" />
          <DutyStat label="Shaft power"     value={fmt(shaftKw, 2)}        unit="kW"    alt={`÷ ${pumpEffPct} % pump eff`} testid="ps-duty-shaft" />
          <DutyStat label="Electrical input" value={fmt(electricalKw, 2)} unit="kW"    alt={`÷ ${motorEffPct} % motor eff`} testid="ps-duty-elec" />
          <DutyStat label="Motor recommendation" value={recommendedMotorKw} unit="kW"   alt="next standard IEC ≥ shaft" testid="ps-duty-motor" highlight />
          <DutyStat label="NPSHa"            value={fmt(npshA, 2)}          unit="m"     alt={`margin ${fmt(npshMargin, 2)} m`} testid="ps-duty-npsha" />
          <DutyStat label="Specify NPSHr ≤"  value={fmt(Math.max(0, npshA - 0.5), 1)} unit="m" alt="0.5 m safety below NPSHa" testid="ps-duty-npshr" />
        </div>

        {/* Supplier specification card */}
        <div className="mt-5 p-5"
          style={{ background: "#0C2A40", color: "#F5F7FA" }}
          data-testid="ps-spec-card"
        >
          <div className="font-mono uppercase mb-3"
            style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "#1FB6C1", fontWeight: 700 }}>
            Supplier-ready pump specification
          </div>
          <p style={{ fontSize: "0.95rem", lineHeight: 1.65 }}>
            Specify a pump rated for{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{fmt(designFlow_m3h, 2)} m³/h</span>{" "}
            at{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{fmt(tdhFinal, 1)} m TDH</span>,
            driven by a{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{recommendedMotorKw} kW</span> motor.
            Required NPSHr ≤{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{fmt(Math.max(0, npshA - 0.5), 1)} m</span>{" "}
            at site altitude{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{altitudeM} m</span>,
            fluid temperature{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{fluidTempC} °C</span>.
            Pump efficiency assumed{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{pumpEffPct} %</span>,
            motor efficiency{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{motorEffPct} %</span>.
          </p>
          <p className="mt-3 font-light" style={{ fontSize: "0.8rem", color: "rgba(245,247,250,0.7)", lineHeight: 1.55 }}>
            The HidroVerse team will validate against the actual pump curve, supplier-specific NPSHr,
            motor IE-class and seasonal demand profile before issuing a procurement spec.
          </p>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────── */}
      {/* SECTION 3b — WHAT-IF OPERATING CURVE                         */}
      {/* Hazen-Williams friction scales as Q^1.852, so the pump head  */}
      {/* climbs non-linearly when flow is upsized. Anchored at 1.0×.  */}
      {/* ─────────────────────────────────────────────────────────── */}
      <div className="mt-6">
        <SectionHead badge="Operating envelope">3b · What-if flow curve</SectionHead>
        <p className="font-light leading-relaxed mb-3" style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.65)" }}>
          Friction scales as <span className="font-mono">Q<sup>1.852</sup></span> in the Hazen-Williams
          formula — so a doubling of flow roughly quadruples the friction component of the TDH.
          The table below re-runs the duty point at <span className="font-mono">0.5× → 2.0×</span> the
          design flow so suppliers see the full operating envelope, not just one point. The
          highlighted row is the current design.
        </p>
        <div className="overflow-x-auto" style={{ border: "1px solid rgba(12,42,64,0.12)" }}>
          <table className="w-full" style={{ fontSize: "0.82rem", borderCollapse: "collapse" }} data-testid="ps-whatif-table">
            <thead>
              <tr style={{ background: "#0C2A40", color: "#FFFFFF" }}>
                {[
                  "Ratio", "Flow (m³/h)", "Friction (m)", "TDH (m)",
                  "Discharge (bar)", "Hydraulic kW", "Shaft kW", "Motor (IEC)",
                ].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-mono uppercase"
                    style={{ fontSize: "0.62rem", letterSpacing: "0.16em", fontWeight: 700 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {whatIfRows.map((r) => {
                const isAnchor = Math.abs(r.ratio - 1.0) < 0.001;
                return (
                  <tr key={r.ratio}
                    data-testid={`ps-whatif-row-${r.ratio.toFixed(2)}`}
                    style={{
                      background: isAnchor ? "rgba(31,182,193,0.10)" : "#FFFFFF",
                      borderTop: "1px solid rgba(12,42,64,0.08)",
                      fontWeight: isAnchor ? 600 : 400,
                      color: "#0C2A40",
                    }}>
                    <td className="px-3 py-2 font-mono" style={{ color: isAnchor ? "#1FB6C1" : "rgba(12,42,64,0.7)" }}>
                      {r.ratio.toFixed(2)}× {isAnchor ? "· design" : ""}
                    </td>
                    <td className="px-3 py-2">{fmt(r.flow_m3h, 2)}</td>
                    <td className="px-3 py-2">{fmt(r.friction_m, 2)}</td>
                    <td className="px-3 py-2">{fmt(r.tdh_m, 1)}</td>
                    <td className="px-3 py-2">{fmt(r.tdh_bar, 2)}</td>
                    <td className="px-3 py-2">{fmt(r.hydKw, 2)}</td>
                    <td className="px-3 py-2">{fmt(r.shaftKw, 2)}</td>
                    <td className="px-3 py-2">{r.motorKw}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-2 font-light" style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.55)" }}>
          Reference only — assumes the same pipework, fittings and static head; pump efficiency
          held at {pumpEffPct} %. Real pumps follow a curve (H falls as Q rises), so the
          intersection of pump-curve and this system-curve is the actual operating point.
        </p>
      </div>

      {/* ─────────────────────────────────────────────────────────── */}
      {/* SECTION 4 — SOLAR (OPTIONAL)                                 */}
      {/* ─────────────────────────────────────────────────────────── */}
      <div className="mt-8">
        <SectionHead>
          4 · Solar PV
          {solarOpen ? " · sizing the array" : ""}
        </SectionHead>

        {!solarOpen ? (
          <button
            type="button"
            onClick={() => setSolarOpen(true)}
            data-testid="ps-solar-open"
            className="w-full p-5 text-left transition-colors"
            style={{ background: "#FCFCFA", border: "1px dashed rgba(12,42,64,0.20)" }}
          >
            <div className="font-mono uppercase mb-1"
              style={{ fontSize: "0.62rem", letterSpacing: "0.22em", color: "#1FB6C1", fontWeight: 700 }}>
              ▸ Want this pump to run off solar?
            </div>
            <p className="font-light" style={{ fontSize: "0.84rem", color: "rgba(12,42,64,0.7)" }}>
              Reveal the PV array sizing module. Pre-filled with the duty point computed above
              ({recommendedMotorKw} kW pump · {fmt(designFlow_m3h, 2)} m³/h · {fmt(tdhFinal, 1)} m TDH)
              and a sensible default site / panel.
            </p>
          </button>
        ) : (
          <div className="p-5" style={{ background: "#FFFFFF", border: "1px solid rgba(12,42,64,0.10)" }}>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <p className="font-light" style={{ fontSize: "0.82rem", color: "rgba(12,42,64,0.65)" }}>
                Inputs are pre-filled from the workflow above. Toggle the override switch to test
                a different pump / flow / head without going back to section 1.
              </p>
              <button
                type="button"
                onClick={() => setSolarOpen(false)}
                data-testid="ps-solar-close"
                className="font-mono uppercase"
                style={{ fontSize: "0.6rem", letterSpacing: "0.18em", color: "rgba(12,42,64,0.6)" }}
              >
                ▴ Hide
              </button>
            </div>

            <div className="mb-4 px-4 py-3 flex items-center justify-between"
              style={{ background: "#FCFCFA", border: "1px solid rgba(12,42,64,0.10)" }}
            >
              <div className="font-mono" style={{ fontSize: "0.78rem", color: "#0C2A40" }}>
                Pump kW <span style={{ fontWeight: 700 }}>{fmt(solarPumpKw, 2)}</span> ·
                Flow <span style={{ fontWeight: 700 }}>{fmt(solarQratedLpm, 0)} L/min</span> ·
                TDH <span style={{ fontWeight: 700 }}>{fmt(solarTdh, 1)} m</span>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="ps-solar-override-toggle">
                <input
                  type="checkbox"
                  checked={solarOverride}
                  onChange={(e) => {
                    if (e.target.checked) {
                      // Seed override values from current computed values
                      setOverridePumpKw(recommendedMotorKw);
                      setOverrideQratedLpm(designFlowLpm);
                      setOverrideHTdh(tdhFinal);
                    }
                    setSolarOverride(e.target.checked);
                  }}
                />
                <span className="font-mono uppercase"
                  style={{ fontSize: "0.6rem", letterSpacing: "0.18em", color: "rgba(12,42,64,0.7)" }}>
                  Override
                </span>
              </label>
            </div>

            {solarOverride && (
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <Field label="Pump kW (override)">
                  <SuffixNum value={overridePumpKw} onChange={setOverridePumpKw} suffix="kW" step="0.1" min={0} testid="ps-solar-pumpkw" />
                </Field>
                <Field label="Design flow (override)">
                  <SuffixNum value={overrideQratedLpm} onChange={setOverrideQratedLpm} suffix="L/min" step="1" min={0} testid="ps-solar-flow" />
                </Field>
                <Field label="TDH (override)">
                  <SuffixNum value={overrideHTdh} onChange={setOverrideHTdh} suffix="m" step="1" min={0} testid="ps-solar-tdh" />
                </Field>
              </div>
            )}

            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Site">
                <Select
                  value={regionId}
                  onChange={setRegionId}
                  options={PV_REGIONS.map((r) => ({ value: r.id, label: `${r.name} · ${r.region}` }))}
                  testid="ps-solar-region"
                />
              </Field>
              <Field label="Panel wattage">
                <Select
                  value={String(panelW)}
                  onChange={(v) => setPanelW(parseFloat(v))}
                  options={PV_PANEL_WATTAGES.map((p) => ({ value: String(p.w), label: p.label }))}
                  testid="ps-solar-panel"
                />
              </Field>
              <Field label="Number of panels">
                <SuffixNum value={pvPanels} onChange={setPvPanels} suffix="× panels" step="1" min={1} testid="ps-solar-count" />
              </Field>
            </div>

            <div className="grid md:grid-cols-3 gap-3 mt-4">
              <DutyStat label="Array size" value={fmt(pvPanels * panelW / 1000, 2)} unit="kWp" alt={`${pvPanels} × ${panelW} W`} testid="ps-solar-kwp" />
              <DutyStat label="Winter delivery" value={fmt(solarSimWinter.volumeLitres, 0)} unit="L/day" alt={`${fmt(solarSimWinter.pumpingHours, 1)} h pumping window`} testid="ps-solar-winter" highlight />
              <DutyStat label="Annual avg delivery" value={fmt(solarSimAnnual.volumeLitres, 0)} unit="L/day" alt={`${fmt(solarSimAnnual.pumpingHours, 1)} h pumping window`} testid="ps-solar-annual" />
            </div>

            <p className="mt-4 font-light leading-relaxed"
              style={{ fontSize: "0.74rem", color: "rgba(12,42,64,0.55)" }}>
              For finer control — staged panel counts, panel-wattage A/B testing,
              VFD cut-in tuning — use the dedicated{" "}
              <a href="/tools/solar/pv-solar"
                style={{ color: "#1FB6C1", textDecoration: "underline", textDecorationColor: "rgba(31,182,193,0.5)" }}>
                PV array sizing calculator
              </a>.
            </p>
          </div>
        )}
      </div>
      </>)}

      {/* ─────────────────────────────────────────────────────────── */}
      {/* EXISTING-SYSTEM (REVERSE) MODE                               */}
      {/* Back-solves a system constant from one measured operating    */}
      {/* point, then forecasts the pump duty at a new target flow.    */}
      {/* ─────────────────────────────────────────────────────────── */}
      {mode === "existing" && (
        <ExistingSystemPanel
          state={{
            ex_pumpBar, setEx_pumpBar,
            ex_endBar, setEx_endBar,
            ex_flow_m3h, setEx_flow_m3h,
            ex_staticM, setEx_staticM,
            ex_newFlow_m3h, setEx_newFlow_m3h,
            ex_newEndBar, setEx_newEndBar,
            ex_safetyPct, setEx_safetyPct,
            ex_pumpEffPct, setEx_pumpEffPct,
            ex_motorEffPct, setEx_motorEffPct,
            ex_currentFriction_m, ex_K,
            ex_newFriction_m, ex_newTdh_m, ex_newPumpBar,
            ex_newHydKw, ex_newShaftKw, ex_newElecKw, ex_newMotorKw,
            ex_status,
          }}
        />
      )}
    </ToolShell>
  );
}

// =============================================================================
// PIPE SEGMENT (live view) — 1:1 mirror of the standalone Hazen-Williams
// advanced tool. Same material list, unit selectors, fittings picker and
// head/bar/velocity outputs — so a user familiar with the friction-loss
// calculator sees identical controls here.
// =============================================================================
function PipeSegmentLive({ state, setState, testidPrefix }) {
  const calc = computeSegment(state);
  const { hL, v, total_len_m, eqlen_m } = calc;
  const id_m = toBase("length_small", state.idVal || 0, state.idUnit) / 1000;
  const dP_bar = hL * 0.0980665;            // 1 m H₂O = 0.0980665 bar
  const lossPer100m = total_len_m > 0 ? (hL / total_len_m) * 100 : 0;

  let vStatus = "ok";
  let vNote   = "Within the recommended 0.6–2.5 m/s range.";
  if (v > 3.0)      { vStatus = "alert"; vNote = "Above 3.0 m/s · erosive and noisy. Up-size the pipe."; }
  else if (v > 2.5) { vStatus = "warn";  vNote = "Above 2.5 m/s · acceptable for short runs only."; }
  else if (v > 0 && v < 0.6) { vStatus = "warn"; vNote = "Below 0.6 m/s · risk of sedimentation."; }

  const set = (patch) => setState({ ...state, ...patch });

  return (
    <>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Pipe material" span={2}>
          <Select
            value={state.material}
            onChange={(val) => set({ material: val })}
            options={PIPE_MATERIALS}
            testid={`${testidPrefix}-material`}
          />
        </Field>
        <Field label="Internal diameter">
          <UnitNumber dim="length_small"
            value={state.idVal}
            onChange={(val) => set({ idVal: val })}
            unit={state.idUnit}
            onUnitChange={(u) => set({ idUnit: u })}
            units={["mm", "cm", "m", "inch"]}
            testid={`${testidPrefix}-id`}
          />
        </Field>
        <Field label="Pipe length">
          <UnitNumber dim="length_big"
            value={state.lenVal}
            onChange={(val) => set({ lenVal: val })}
            unit={state.lenUnit}
            onUnitChange={(u) => set({ lenUnit: u })}
            units={["m", "ft", "km"]}
            testid={`${testidPrefix}-len`}
          />
        </Field>
        <Field label="Flow rate" span={2}>
          <UnitNumber dim="flow"
            value={state.flowVal}
            onChange={(val) => set({ flowVal: val })}
            unit={state.flowUnit}
            onUnitChange={(u) => set({ flowUnit: u })}
            units={["Lps", "Lpm", "m3h", "m3d", "gpm"]}
            testid={`${testidPrefix}-flow`}
          />
        </Field>

        {/* Fittings — Crane TP-410 equivalent-length picker (same component
            used in the standalone Hazen-Williams tool). */}
        <FittingsPicker
          qtyMap={state.fittings || {}}
          onChange={(m) => set({ fittings: m })}
          id_m={id_m}
          testid={`${testidPrefix}-fittings`}
        />
      </div>

      {/* Per-segment outputs: head loss + pressure loss + velocity.
          Same 3-card layout the Hazen-Williams tool uses. */}
      <div className="grid md:grid-cols-3 gap-3 mt-4">
        <SegOut
          label="Head loss"
          value={fmt(hL, 2)} unit="m H₂O"
          alts={hL ? [{ value: fmt(fromBase("length_big", hL, "ft"), 2), unit: "ft H₂O" }] : null}
          note={total_len_m > 0
            ? `≈ ${fmt(lossPer100m, 2)} m per 100 m · over ${fmt(total_len_m, 1)} m total${eqlen_m > 0 ? ` (inc. ${fmt(eqlen_m, 1)} m for fittings)` : ""}`
            : null}
          testid={`${testidPrefix}-headloss`}
        />
        <SegOut
          label="Pressure loss"
          value={fmt(dP_bar, 2)} unit="bar"
          alts={dP_bar ? altsFor("pressure", dP_bar, ["kPa", "psi"], 1) : null}
          testid={`${testidPrefix}-bar`}
        />
        <SegOut
          label="Velocity"
          value={fmt(v, 2)} unit="m/s"
          alts={v ? [{ value: fmt(fromBase("velocity", v, "fps"), 2), unit: "ft/s" }] : null}
          status={vStatus} note={vNote}
          testid={`${testidPrefix}-velocity`}
        />
      </div>
    </>
  );
}

// Compact 3-card result tile — Hazen-Williams style.
function SegOut({ label, value, unit, alts, note, status, testid }) {
  const borderColor =
    status === "alert" ? "rgba(184,92,56,0.45)"
    : status === "warn" ? "rgba(204,142,52,0.45)"
    : "rgba(12,42,64,0.10)";
  const noteColor =
    status === "alert" ? "#B85C38"
    : status === "warn" ? "#8A6B22"
    : "rgba(12,42,64,0.55)";
  return (
    <div
      className="px-4 py-3"
      style={{ background: "#FCFCFA", border: `1px solid ${borderColor}` }}
      data-testid={testid}
    >
      <div className="font-mono uppercase"
        style={{ fontSize: "0.58rem", letterSpacing: "0.2em", color: "rgba(12,42,64,0.55)" }}>
        {label}
      </div>
      <div className="flex items-baseline gap-1.5 mt-1">
        <span style={{ fontSize: "1.25rem", fontWeight: 600, color: "#0C2A40" }}>{value}</span>
        <span className="font-mono"
          style={{ fontSize: "0.7rem", color: "rgba(12,42,64,0.6)" }}>{unit}</span>
      </div>
      {alts && alts.length > 0 && (
        <div className="mt-1 font-mono" style={{ fontSize: "0.7rem", color: "rgba(12,42,64,0.55)" }}>
          {alts.map((a, i) => (
            <span key={i}>
              {i > 0 ? " · " : "= "}{a.value} {a.unit}
            </span>
          ))}
        </div>
      )}
      {note && (
        <div className="mt-1 font-light" style={{ fontSize: "0.7rem", color: noteColor, lineHeight: 1.3 }}>
          {note}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// SMALL OUTPUT STAT CARDS
// =============================================================================
function NpshStat({ label, value, unit, detail, highlight, testid }) {
  return (
    <div
      className="px-4 py-3"
      style={{
        background: highlight ? "#F3FBFB" : "#FCFCFA",
        border: `1px solid ${highlight ? "rgba(31,182,193,0.35)" : "rgba(12,42,64,0.10)"}`,
      }}
      data-testid={testid}
    >
      <div className="font-mono uppercase" style={{ fontSize: "0.58rem", letterSpacing: "0.2em", color: "rgba(12,42,64,0.55)" }}>
        {label}
      </div>
      <div className="flex items-baseline gap-1.5 mt-1">
        <span style={{ fontSize: "1.35rem", fontWeight: 600, color: "#0C2A40" }}>{fmt(value, 2)}</span>
        <span className="font-mono" style={{ fontSize: "0.7rem", color: "rgba(12,42,64,0.6)" }}>{unit}</span>
      </div>
      {detail && (
        <div className="mt-1 font-light" style={{ fontSize: "0.7rem", color: "rgba(12,42,64,0.55)" }}>
          {detail}
        </div>
      )}
    </div>
  );
}

function DutyStat({ label, value, unit, alt, highlight, testid }) {
  return (
    <div
      className="px-4 py-3"
      style={{
        background: highlight ? "rgba(31,182,193,0.10)" : "#FFFFFF",
        border: `1px solid ${highlight ? "rgba(31,182,193,0.40)" : "rgba(12,42,64,0.10)"}`,
      }}
      data-testid={testid}
    >
      <div className="font-mono uppercase" style={{ fontSize: "0.58rem", letterSpacing: "0.2em", color: "rgba(12,42,64,0.55)" }}>
        {label}
      </div>
      <div className="flex items-baseline gap-1.5 mt-1">
        <span style={{ fontSize: "1.35rem", fontWeight: 700, color: "#0C2A40" }}>{value}</span>
        <span className="font-mono" style={{ fontSize: "0.7rem", color: "rgba(12,42,64,0.6)" }}>{unit}</span>
      </div>
      {alt && (
        <div className="mt-1 font-light" style={{ fontSize: "0.7rem", color: "rgba(12,42,64,0.55)" }}>
          {alt}
        </div>
      )}
    </div>
  );
}


// =============================================================================
// EXISTING-SYSTEM (REVERSE) PANEL — back-solves the system constant from a
// measured operating point and forecasts the pump duty at a new target flow.
//
//   Step 1 — current friction:  f_now = (P_pump − P_end) × 10.197 − static
//   Step 2 — system constant:   K     = f_now / Q_now^1.852
//   Step 3 — new friction:      f_new = K · Q_new^1.852
//   Step 4 — new TDH:           static + f_new + (P_end_new × 10.197) × (1+SF)
//   Step 5 — new pump bar:      TDH_new ÷ 10.197
// =============================================================================
function ExistingSystemPanel({ state: s }) {
  const verdictBg =
    s.ex_status === "alert" ? "#FFF5EE"
    : s.ex_status === "warn" ? "#FFFAEB"
    : "#F0F9EE";
  const verdictBorder =
    s.ex_status === "alert" ? "rgba(184,92,56,0.40)"
    : s.ex_status === "warn" ? "rgba(204,142,52,0.40)"
    : "rgba(91,136,71,0.30)";
  const verdictColor =
    s.ex_status === "alert" ? "#B85C38"
    : s.ex_status === "warn" ? "#8A6B22"
    : "#5B8847";

  return (
    <div data-testid="ps-existing-panel">
      {/* SECTION A — measured operating point */}
      <SectionHead>1 · Measured operating point</SectionHead>
      <p className="font-light leading-relaxed mb-3" style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.65)" }}>
        Plug in the readings from site. Pump discharge pressure is what the
        pressure gauge at the pump shows; end-of-line pressure is the gauge
        reading at the furthest emitter or the last consumer. Static head is
        the vertical lift from the water source up to the highest discharge
        (use 0 for a closed loop or a tank-to-tank job).
      </p>
      <div className="p-5 mb-4"
        style={{ background: "#FFFFFF", border: "1px solid rgba(12,42,64,0.08)", borderLeft: "3px solid #0C2A40" }}>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Field label="Pump discharge pressure" hint="Reading on the pressure gauge mounted at the pump outlet, today.">
            <SuffixNum value={s.ex_pumpBar} onChange={s.setEx_pumpBar} suffix="bar" step="0.1" min={0} testid="ex-pump-bar" />
          </Field>
          <Field label="End-of-line pressure" hint="Reading on the pressure gauge at the last consumer / furthest emitter, today.">
            <SuffixNum value={s.ex_endBar} onChange={s.setEx_endBar} suffix="bar" step="0.1" min={0} testid="ex-end-bar" />
          </Field>
          <Field label="Current flow" hint="Measured flow at the pump or the consumer, today.">
            <SuffixNum value={s.ex_flow_m3h} onChange={s.setEx_flow_m3h} suffix="m³/h" step="0.5" min={0} testid="ex-flow" />
          </Field>
          <Field label="Static head" hint="Vertical lift from source up to the discharge level. 0 for a closed loop or level-to-level job.">
            <SuffixNum value={s.ex_staticM} onChange={s.setEx_staticM} suffix="m" step="1" min={0} testid="ex-static" />
          </Field>
        </div>
        <div className="grid md:grid-cols-3 gap-3 mt-4">
          <SegOut label="Current friction loss"
            value={fmt(s.ex_currentFriction_m, 2)} unit="m H₂O"
            alts={s.ex_currentFriction_m ? [{ value: fmt(s.ex_currentFriction_m * 0.0980665, 2), unit: "bar" }] : null}
            note={`(${fmt(s.ex_pumpBar, 2)} − ${fmt(s.ex_endBar, 2)}) bar × 10.197 m/bar − ${s.ex_staticM} m static.`}
            testid="ex-current-friction" />
          <SegOut label="System constant K"
            value={s.ex_K !== null ? s.ex_K.toExponential(3) : "—"} unit="m·(m³/h)⁻¹·⁸⁵²"
            note="Back-solved from f = K · Q^1.852. Stays constant for the same pipework."
            testid="ex-K" />
          <SegOut label="Current TDH (incl. static)"
            value={fmt(s.ex_currentFriction_m + s.ex_staticM + (s.ex_endBar * 10.197162), 2)} unit="m"
            note="Static + friction + end pressure."
            testid="ex-current-tdh" />
        </div>
      </div>

      {/* SECTION B — new target */}
      <div className="mt-6">
        <SectionHead badge="Forecast">2 · New target</SectionHead>
        <p className="font-light leading-relaxed mb-3" style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.65)" }}>
          What flow do you want, and at what end-of-line pressure? Friction
          climbs as <span className="font-mono">(Q<sub>new</sub>/Q<sub>now</sub>)<sup>1.852</sup></span>
          {" "}— so a 50 % flow uplift roughly doubles the friction loss the new
          pump has to overcome.
        </p>
        <div className="p-5"
          style={{ background: "#FFFFFF", border: "1px solid rgba(12,42,64,0.08)", borderLeft: "3px solid #1FB6C1" }}>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Field label="Desired new flow" hint="What flow you actually want the system to deliver after the retrofit.">
              <SuffixNum value={s.ex_newFlow_m3h} onChange={s.setEx_newFlow_m3h} suffix="m³/h" step="0.5" min={0} testid="ex-new-flow" />
            </Field>
            <Field label="Desired end-of-line pressure" hint="Pressure you want to see at the last consumer / furthest emitter after the retrofit.">
              <SuffixNum value={s.ex_newEndBar} onChange={s.setEx_newEndBar} suffix="bar" step="0.1" min={0} testid="ex-new-end-bar" />
            </Field>
            <Field label="Pump efficiency" hint="65–75 % typical for vertical multistage at BEP.">
              <SuffixNum value={s.ex_pumpEffPct} onChange={s.setEx_pumpEffPct} suffix="%" step="1" min={1} testid="ex-pumpeff" />
            </Field>
            <Field label="Motor efficiency" hint="IE3 ≈ 92 %, IE4 ≈ 95 % at design load.">
              <SuffixNum value={s.ex_motorEffPct} onChange={s.setEx_motorEffPct} suffix="%" step="1" min={1} testid="ex-motoreff" />
            </Field>
            <Field label="Safety margin" hint="Typical 10 – 15 % over computed TDH to absorb pipe ageing and minor flow excursions.">
              <SuffixNum value={s.ex_safetyPct} onChange={s.setEx_safetyPct} suffix="%" step="1" min={0} testid="ex-safety" />
            </Field>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            <SegOut label="Forecast friction at new flow"
              value={fmt(s.ex_newFriction_m, 2)} unit="m H₂O"
              alts={s.ex_newFriction_m ? [{ value: fmt(s.ex_newFriction_m * 0.0980665, 2), unit: "bar" }] : null}
              note={s.ex_flow_m3h > 0
                ? `Scales by (${fmt(s.ex_newFlow_m3h, 2)} / ${fmt(s.ex_flow_m3h, 2)})^1.852 = ${fmt(Math.pow((s.ex_newFlow_m3h || 0) / s.ex_flow_m3h, 1.852), 3)}×.`
                : null}
              testid="ex-new-friction" />
            <SegOut label="Required new TDH"
              value={fmt(s.ex_newTdh_m, 1)} unit="m"
              alts={s.ex_newTdh_m ? [{ value: fmt(s.ex_newTdh_m * 0.0980665, 2), unit: "bar" }] : null}
              note={`Static + friction + end pressure, × (1 + ${s.ex_safetyPct} % SF).`}
              testid="ex-new-tdh" />
            <SegOut label="Required new pump discharge"
              value={fmt(s.ex_newPumpBar, 2)} unit="bar"
              status={s.ex_status}
              note={s.ex_newPumpBar !== null
                ? `Up from ${fmt(s.ex_pumpBar, 2)} bar today — pressure jump ${fmt(s.ex_newPumpBar - s.ex_pumpBar, 2)} bar.`
                : null}
              testid="ex-new-bar" />
            <SegOut label="Recommended motor (IEC)"
              value={s.ex_newMotorKw || "—"} unit="kW"
              alts={s.ex_newShaftKw !== null ? [{ value: fmt(s.ex_newShaftKw, 2), unit: "kW shaft" }] : null}
              note={s.ex_newElecKw !== null ? `Electrical input ≈ ${fmt(s.ex_newElecKw, 2)} kW.` : null}
              testid="ex-new-motor" />
          </div>
        </div>
      </div>

      {/* SECTION C — supplier-ready retrofit specification */}
      {s.ex_newPumpBar !== null && s.ex_K !== null && (
        <div className="mt-6 p-5"
          style={{ background: "#0C2A40", color: "#F5F7FA" }}
          data-testid="ex-spec-card">
          <div className="font-mono uppercase mb-3"
            style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "#1FB6C1", fontWeight: 700 }}>
            Pump retrofit specification
          </div>
          <p style={{ fontSize: "0.95rem", lineHeight: 1.65 }}>
            Existing system: pump at{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{fmt(s.ex_pumpBar, 2)} bar</span>,
            end-of-line at{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{fmt(s.ex_endBar, 2)} bar</span>,
            flow{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{fmt(s.ex_flow_m3h, 2)} m³/h</span>,
            static{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{s.ex_staticM} m</span>{" "}
            ⇒ current friction{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{fmt(s.ex_currentFriction_m, 2)} m</span>{" "}
            (<span style={{ fontWeight: 700, color: "#1FB6C1" }}>{fmt(s.ex_currentFriction_m * 0.0980665, 2)} bar</span>).{" "}
            To deliver{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{fmt(s.ex_newFlow_m3h, 2)} m³/h</span>{" "}
            at{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{fmt(s.ex_newEndBar, 2)} bar</span>{" "}
            end-of-line, friction climbs to{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{fmt(s.ex_newFriction_m, 2)} m</span>{" "}
            (Q<sup>1.852</sup> scaling). New pump must produce{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{fmt(s.ex_newFlow_m3h, 2)} m³/h at {fmt(s.ex_newPumpBar, 2)} bar discharge</span>{" "}
            (<span style={{ fontWeight: 700, color: "#1FB6C1" }}>{fmt(s.ex_newTdh_m, 1)} m TDH</span>){" "}
            — a{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{s.ex_newMotorKw} kW</span> IEC motor at{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{s.ex_pumpEffPct} %</span> pump and{" "}
            <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{s.ex_motorEffPct} %</span> motor efficiency.
          </p>
          <p className="mt-3 font-light"
            style={{ fontSize: "0.8rem", color: "rgba(245,247,250,0.7)", lineHeight: 1.55 }}>
            Reference only — assumes the same pipework and no major leaks. HidroVerse will
            validate against the actual pump curve, supplier-specific NPSHr and a fresh
            site measurement before issuing a procurement spec.
          </p>
        </div>
      )}

      {/* Verdict strip */}
      <div className="mt-4 px-4 py-3"
        style={{ background: verdictBg, border: `1px solid ${verdictBorder}` }}
        data-testid="ex-verdict">
        <div className="font-mono uppercase mb-1"
          style={{ fontSize: "0.6rem", letterSpacing: "0.2em", color: verdictColor, fontWeight: 700 }}>
          {s.ex_status === "ok"    ? "Verdict · within envelope"
          : s.ex_status === "warn" ? "Verdict · pump-pressure uplift required"
          :                          "Verdict · large pump-pressure jump · review pipework"}
        </div>
        <p className="font-light leading-snug" style={{ fontSize: "0.78rem", color: "#0C2A40" }}>
          {s.ex_K === null
            ? "Need a positive (P_pump − P_end) and a current flow > 0 to back-solve the system constant."
            : s.ex_status === "alert"
              ? `Required pump discharge (${fmt(s.ex_newPumpBar, 2)} bar) is more than double today's reading. The pipework may be undersized for this new flow — consider upsizing the mainline before specifying a bigger pump.`
              : s.ex_status === "warn"
                ? `Required pump discharge (${fmt(s.ex_newPumpBar, 2)} bar) is above today's reading by ${fmt((s.ex_newPumpBar || 0) - s.ex_pumpBar, 2)} bar. The new pump must hold the new flow at this higher pressure.`
                : `Required pump discharge (${fmt(s.ex_newPumpBar, 2)} bar) is at or below today's reading — flow uplift is achievable with the existing pressure envelope; check pump curve at the new flow.`}
        </p>
      </div>
    </div>
  );
}
